// apps/client/src/pages/leads/website/WebsiteConnectionsPage.tsx
//
// Lead Capture > Website (/leads/website).
//
// A company connects one or more websites. Each connection has a public site
// key, the exact website addresses allowed to post, a protection mode and
// which of the three forms (enquiry, newsletter, referral) are on. This page
// lists connections, creates and edits them, rotates the site key, turns a
// connection off and on, gives the install snippet, and shows 30-day health.
//
// Access: the tenant's super_admin writes; marketing reads (list, health,
// snippet). Founders never manage connections here (founder = admin portal
// only). The callable (websiteConnectionAction) enforces the same split
// server side.
//
// Spec: docs/plans/2026-10-23-WEBSITE-FORMS-PLATFORM.md §1, §3 and §9.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  AlertTriangle,
  Check,
  Copy,
  Globe,
  KeyRound,
  Pencil,
  Plus,
  Power,
  ShieldCheck,
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type { WebsiteConnectionInput } from '@valutten/shared-schemas/dist/websiteConnection';
import type { ClassificationOptions } from '@valutten/shared-schemas';
import type { RoutingPreviewRules } from '@valutten/shared-schemas/dist/websiteRoutingPreview';
import { PageHeader } from '../../../components/common/PageHeader';
import { Skeleton } from '../../../components/common/SkeletonLoader';
import { Spinner } from '../../../components/common/Spinner';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { db, functions } from '../../../lib/firebase';
import { firebaseConfig } from '../../../firebase/config';
import {
  timestampToMillis,
  useWebsiteConnections,
  type WebsiteConnectionSaveInput,
  type WebsiteConnectionView,
} from '../../../hooks/useWebsiteConnections';
import { WebsiteConnectionEditor, type SelectOption } from './WebsiteConnectionEditor';
import { WebsiteHealthPanel } from './WebsiteHealthPanel';
import { WebsiteRoutingPreview, type WebsiteRoutingContext } from './WebsiteRoutingPreview';
import { FORM_LABELS, MODE_LABELS, buildInstallSnippet, websiteFormsApiBase } from './websiteConnectionForm';

type OutletCtx = {
  userData?: { companyId?: string; role?: string };
  offices?: Array<{ id?: string; name?: string; officeId?: string; officeName?: string }>;
};

/** Fallback when the tenant has no lead_taxonomy/default doc. */
const FALLBACK_CATEGORIES: SelectOption[] = [
  { id: 'finance', label: 'Finance' },
  { id: 'home_loan', label: 'Home Loan' },
];

type PendingConfirm =
  | { kind: 'rotate'; connection: WebsiteConnectionView }
  | { kind: 'disable'; connection: WebsiteConnectionView }
  | null;

const FORM_KEYS = ['enquiry', 'newsletter', 'referral'] as const;

function formatExpiry(value: unknown): string {
  const ms = timestampToMillis(value);
  if (ms === null) return 'soon';
  return new Date(ms).toLocaleString('en-AU', {
    timeZone: 'Australia/Brisbane',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const StatusPill: React.FC<{ connection: WebsiteConnectionView }> = ({ connection }) =>
  connection.status === 'active' ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.625rem] font-semibold uppercase tracking-wide border bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
      Active
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.625rem] font-semibold uppercase tracking-wide border bg-gray-500/15 text-gray-300 border-gray-500/30">
      Off
    </span>
  );

const ModePill: React.FC<{ connection: WebsiteConnectionView }> = ({ connection }) => {
  const palette =
    connection.mode === 'enforce'
      ? 'bg-blue-500/15 text-blue-300 border-blue-500/30'
      : connection.mode === 'observe'
        ? 'bg-violet-500/15 text-violet-300 border-violet-500/30'
        : 'bg-amber-500/15 text-amber-300 border-amber-500/30';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[0.625rem] font-semibold uppercase tracking-wide border ${palette}`}>
      <ShieldCheck className="w-3 h-3" />
      {MODE_LABELS[connection.mode]?.label ?? connection.mode}
    </span>
  );
};

const InstallSnippet: React.FC<{ connection: WebsiteConnectionView }> = ({ connection }) => {
  const apiBase = websiteFormsApiBase(firebaseConfig.projectId);
  const [copied, setCopied] = useState<'key' | 'snippet' | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  const snippet = apiBase ? buildInstallSnippet(connection.siteKey, apiBase) : '';

  const copy = async (what: 'key' | 'snippet', text: string) => {
    setCopyError(null);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      window.setTimeout(() => setCopied((c) => (c === what ? null : c)), 2000);
    } catch {
      setCopyError('Copy is blocked in this browser. Select the text and copy it instead.');
    }
  };

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-5 space-y-4">
      <div>
        <p className="text-xs font-semibold text-gray-400 mb-1">Site key</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 min-w-0 truncate rounded-md bg-gray-900 border border-gray-700 px-3 py-2 text-sm font-mono">
            {connection.siteKey}
          </code>
          <button type="button" onClick={() => copy('key', connection.siteKey)}
            className="px-3 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-xs inline-flex items-center gap-1" aria-label="Copy site key">
            {copied === 'key' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied === 'key' ? 'Copied' : 'Copy'}
          </button>
        </div>
        <p className="text-[0.6875rem] text-gray-500 mt-1">
          The site key is public. It is printed in your website&apos;s HTML and only works from the addresses listed on this connection.
        </p>
        {(connection.previousSiteKeys ?? []).length > 0 && (
          <ul className="mt-2 space-y-1">
            {connection.previousSiteKeys.map((p) => (
              <li key={p.key} className="text-xs text-gray-400">
                Previous key <span className="font-mono">{p.key.slice(0, 12)}…</span> keeps working until {formatExpiry(p.expiresAt)}.
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs font-semibold text-gray-400">Install snippet</p>
          {apiBase && (
            <button type="button" onClick={() => copy('snippet', snippet)}
              className="px-3 py-1.5 rounded-md bg-gray-700 hover:bg-gray-600 text-xs inline-flex items-center gap-1" aria-label="Copy install snippet">
              {copied === 'snippet' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied === 'snippet' ? 'Copied' : 'Copy snippet'}
            </button>
          )}
        </div>
        {apiBase ? (
          <>
            <pre className="rounded-md bg-gray-900 border border-gray-700 p-3 text-xs font-mono overflow-x-auto whitespace-pre">{snippet}</pre>
            <p className="text-[0.6875rem] text-gray-500 mt-1">
              Paste before the closing &lt;/body&gt; tag on every page with a form. Your web developer then calls
              ValuttenForms.submit for each form; the developer guide lists the fields.
            </p>
          </>
        ) : (
          <p className="text-sm text-amber-300">This environment has no public forms endpoint, so there is no snippet to install.</p>
        )}
        {copyError && <p className="text-xs text-red-400 mt-1">{copyError}</p>}
      </div>
    </div>
  );
};

const WebsiteConnectionsPage: React.FC = () => {
  const ctx = useOutletContext<OutletCtx>();
  const companyId = ctx.userData?.companyId;
  const role = ctx.userData?.role ?? '';
  const canWrite = role === 'super_admin';
  const canRead = canWrite || role === 'marketing';

  const {
    connections,
    referralPartners,
    loading,
    error,
    refresh,
    create,
    update,
    rotateSiteKey,
    setStatus,
    resumeWelcome,
    loadHealth,
  } = useWebsiteConnections(canRead ? companyId : undefined);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editor, setEditor] = useState<{ open: boolean; connection: WebsiteConnectionView | null }>({
    open: false,
    connection: null,
  });
  const [pending, setPending] = useState<PendingConfirm>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [healthKey, setHealthKey] = useState(0);
  const [categories, setCategories] = useState<SelectOption[]>(FALLBACK_CATEGORIES);

  // Keep a valid selection: the first connection by default, and fall back
  // when the selected one disappears after a refresh.
  useEffect(() => {
    if (connections.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !connections.some((c) => c.id === selectedId)) setSelectedId(connections[0].id);
  }, [connections, selectedId]);

  // Service categories for the enquiry form, from the tenant's taxonomy
  // (same source and fallback rule as the Setup Wizard).
  useEffect(() => {
    if (!companyId || !canWrite) return;
    let cancelled = false;
    getDoc(doc(db, `companies/${companyId}/lead_taxonomy/default`))
      .then((snap) => {
        if (cancelled || !snap.exists()) return;
        const d = snap.data() as { serviceCategories?: Array<{ id?: string; label?: string; order?: number; active?: boolean }> };
        const cats = (d.serviceCategories ?? [])
          .filter((c): c is { id: string; label?: string; order?: number; active?: boolean } => !!c && typeof c.id === 'string' && c.active !== false)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((c) => ({ id: c.id, label: c.label?.trim() || c.id }));
        if (cats.length > 0) setCategories(cats);
      })
      .catch((err) => console.warn('[WebsiteConnectionsPage] lead_taxonomy read failed; using defaults', err));
    return () => {
      cancelled = true;
    };
  }, [companyId, canWrite]);

  // Routing preview inputs, read the same way the Routing page reads them:
  // the rules doc companies/{c}/lead_routing_rules/default, and the
  // classification options + routing_v2 mode from getLeadTaxonomy. Either
  // failing shows "could not be checked", never a false all clear.
  const [classification, setClassification] = useState<Record<string, ClassificationOptions> | null>(null);
  const [routing, setRouting] = useState<WebsiteRoutingContext>({ status: 'loading', rules: null, routingV2Mode: 'off' });
  useEffect(() => {
    if (!companyId || !canRead) return;
    let cancelled = false;
    const taxonomy = httpsCallable<
      { action: 'getLeadTaxonomy'; companyId: string },
      { classification?: Record<string, ClassificationOptions> | null; routingV2Mode?: unknown }
    >(functions, 'leadCaptureAction')({ action: 'getLeadTaxonomy', companyId });
    const rules = getDoc(doc(db, `companies/${companyId}/lead_routing_rules/default`));
    Promise.all([taxonomy, rules])
      .then(([t, snap]) => {
        if (cancelled) return;
        setClassification(t.data?.classification ?? null);
        setRouting({
          status: 'ready',
          rules: snap.exists() ? ((snap.data() ?? null) as RoutingPreviewRules | null) : null,
          routingV2Mode: t.data?.routingV2Mode,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[WebsiteConnectionsPage] routing preview unavailable', err);
        setRouting({ status: 'error', rules: null, routingV2Mode: 'off' });
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, canRead]);

  const offices: SelectOption[] = useMemo(
    () =>
      (ctx.offices ?? [])
        .map((o) => ({ id: o.id ?? o.officeId ?? '', label: o.name ?? o.officeName ?? o.id ?? o.officeId ?? '' }))
        .filter((o) => o.id)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [ctx.offices],
  );

  const selected = connections.find((c) => c.id === selectedId) ?? null;

  const labelFor = useCallback(
    (list: SelectOption[], id: string | null | undefined) => (id ? list.find((o) => o.id === id)?.label ?? id : null),
    [],
  );

  const handleSave = async (input: WebsiteConnectionInput | WebsiteConnectionSaveInput) => {
    const saved = editor.connection ? await update(editor.connection.id, input) : await create(input);
    setEditor({ open: false, connection: null });
    setSelectedId(saved.id);
    setNotice(editor.connection ? 'Connection saved.' : 'Connection created. Copy the install snippet below to your website.');
  };

  const runConfirmed = async () => {
    if (!pending) return;
    const { kind, connection } = pending;
    setPending(null);
    setActionBusy(true);
    setActionError(null);
    setNotice(null);
    try {
      if (kind === 'rotate') {
        await rotateSiteKey(connection.id);
        setNotice('New site key issued. Update your website within 24 hours; the old key stops working after that.');
      } else {
        await setStatus(connection.id, 'disabled');
        setNotice(`${connection.label} is off. The website's forms now get an error until you turn it back on.`);
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'That did not work. Please try again.');
    } finally {
      setActionBusy(false);
    }
  };

  const enable = async (connection: WebsiteConnectionView) => {
    setActionBusy(true);
    setActionError(null);
    setNotice(null);
    try {
      await setStatus(connection.id, 'active');
      setNotice(`${connection.label} is on.`);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'That did not work. Please try again.');
    } finally {
      setActionBusy(false);
    }
  };

  const handleResumeWelcome = async (connectionId: string) => {
    await resumeWelcome(connectionId);
    setNotice('Welcome emails resumed.');
  };

  if (!canRead) {
    return (
      <div className="space-y-6">
        <PageHeader icon={Globe} title="Website" />
        <div className="min-h-[50vh] rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-amber-300 text-sm">
          You do not have permission to view this page.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Globe}
        title="Website"
        subtitle="Connect your website's enquiry, newsletter and referral forms. Submissions arrive here as leads, subscribers and referrals, with consent recorded."
        badge={canWrite ? undefined : 'Read only'}
        onRefresh={() => {
          refresh();
          setHealthKey((k) => k + 1);
        }}
        isRefreshing={loading}
        actions={
          canWrite ? (
            <button type="button" onClick={() => setEditor({ open: true, connection: null })}
              className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-sm font-medium inline-flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Connect a website
            </button>
          ) : undefined
        }
      />

      {error && (
        <div role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-red-300 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {actionError && (
        <div role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-red-300 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{actionError}</span>
        </div>
      )}
      {notice && (
        <div role="status" className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-emerald-300 text-sm">
          {notice}
        </div>
      )}

      {loading && connections.length === 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" aria-label="Loading connections">
          <Skeleton height="7rem" />
          <Skeleton height="7rem" />
        </div>
      ) : connections.length === 0 && !error ? (
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-8 text-center">
          <Globe className="w-8 h-8 text-gray-500 mx-auto mb-3" />
          <p className="text-sm font-medium">No website connected yet</p>
          <p className="text-sm text-gray-400 mt-1">
            {canWrite
              ? 'Connect a website to receive enquiries, newsletter sign-ups and referrals from its forms.'
              : 'A super admin can connect your website.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {connections.map((c) => {
            const isSelected = c.id === selectedId;
            return (
              <button key={c.id} type="button" onClick={() => setSelectedId(c.id)} aria-pressed={isSelected}
                className={`text-left rounded-lg border p-4 transition-colors ${isSelected ? 'border-blue-500/60 bg-blue-500/10' : 'border-gray-700 bg-gray-800/40 hover:bg-gray-800/70'}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium truncate">{c.label}</p>
                  <StatusPill connection={c} />
                </div>
                <p className="text-xs text-gray-400 mt-1 truncate">{(c.allowedOrigins ?? []).join(', ')}</p>
                <div className="flex flex-wrap items-center gap-1.5 mt-3">
                  <ModePill connection={c} />
                  {c.environment === 'staging' && (
                    <span className="px-2 py-0.5 rounded-full text-[0.625rem] font-semibold uppercase tracking-wide border border-gray-600 text-gray-300">
                      Test
                    </span>
                  )}
                  {FORM_KEYS.filter((f) => c.forms?.[f]?.enabled).map((f) => (
                    <span key={f} className="px-2 py-0.5 rounded-full text-[0.625rem] border border-gray-600 text-gray-300">
                      {FORM_LABELS[f]}
                    </span>
                  ))}
                  {c.welcome?.pausedReason && (
                    <span className="px-2 py-0.5 rounded-full text-[0.625rem] font-semibold border bg-amber-500/15 text-amber-300 border-amber-500/30">
                      Welcome paused
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selected && (
        <section className="space-y-4" aria-label={`${selected.label} details`}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">{selected.label}</h2>
              <p className="text-xs text-gray-400">
                {MODE_LABELS[selected.mode]?.help}
                {selected.forms?.enquiry?.enabled && (
                  <>
                    {' '}Enquiries: {labelFor(categories, selected.forms.enquiry.serviceCategory) ?? 'any service'},{' '}
                    {labelFor(offices, selected.forms.enquiry.officeId) ?? 'company-wide'}.
                  </>
                )}
              </p>
            </div>
            {canWrite && (
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => setEditor({ open: true, connection: selected })} disabled={actionBusy}
                  className="px-3 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-sm inline-flex items-center gap-2 disabled:opacity-50">
                  <Pencil className="w-4 h-4" />
                  Edit
                </button>
                <button type="button" onClick={() => setPending({ kind: 'rotate', connection: selected })} disabled={actionBusy}
                  className="px-3 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-sm inline-flex items-center gap-2 disabled:opacity-50">
                  <KeyRound className="w-4 h-4" />
                  Rotate site key
                </button>
                {selected.status === 'active' ? (
                  <button type="button" onClick={() => setPending({ kind: 'disable', connection: selected })} disabled={actionBusy}
                    className="px-3 py-2 rounded-md bg-gray-700 hover:bg-red-700 text-sm inline-flex items-center gap-2 disabled:opacity-50">
                    {actionBusy ? <Spinner size="sm" /> : <Power className="w-4 h-4" />}
                    Turn off
                  </button>
                ) : (
                  <button type="button" onClick={() => enable(selected)} disabled={actionBusy}
                    className="px-3 py-2 rounded-md bg-emerald-700 hover:bg-emerald-600 text-sm inline-flex items-center gap-2 disabled:opacity-50">
                    {actionBusy ? <Spinner size="sm" /> : <Power className="w-4 h-4" />}
                    Turn on
                  </button>
                )}
              </div>
            )}
          </div>

          {(selected.forms?.enquiry?.enabled || (selected.forms?.referral?.enabled && selected.forms.referral.routeAsLead)) && (
            <div className="space-y-2" aria-label="Where website leads go">
              {selected.forms?.enquiry?.enabled && (
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-gray-400">Enquiry form</p>
                  <WebsiteRoutingPreview
                    context={routing}
                    testId="page-routing-enquiry"
                    target={{
                      serviceCategory: selected.forms.enquiry.serviceCategory ?? null,
                      officeId: selected.forms.enquiry.officeId ?? null,
                    }}
                  />
                </div>
              )}
              {selected.forms?.enquiry?.enabled && (selected.forms.enquiry.variants ?? []).map((v) => (
                <div key={v.key} className="space-y-1">
                  <p className="text-xs font-semibold text-gray-400">
                    {v.label} <span className="font-mono font-normal text-gray-500">({v.key})</span>
                  </p>
                  <WebsiteRoutingPreview
                    context={routing}
                    target={{ serviceCategory: v.serviceCategory ?? null, officeId: v.officeId ?? null, preset: v.preset }}
                  />
                </div>
              ))}
              {selected.forms?.referral?.enabled && selected.forms.referral.routeAsLead && (
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-gray-400">Referral form leads</p>
                  <WebsiteRoutingPreview
                    context={routing}
                    target={{
                      serviceCategory: selected.forms.referral.serviceCategory ?? null,
                      officeId: selected.forms.referral.officeId ?? null,
                    }}
                  />
                </div>
              )}
            </div>
          )}

          <InstallSnippet connection={selected} />

          <WebsiteHealthPanel
            connection={selected}
            canWrite={canWrite}
            loadHealth={loadHealth}
            onResumeWelcome={handleResumeWelcome}
            reloadKey={healthKey}
          />
        </section>
      )}

      {editor.open && (
        <WebsiteConnectionEditor
          show
          connection={editor.connection}
          serviceCategories={categories}
          offices={offices}
          referralPartners={referralPartners}
          classification={classification}
          routing={routing}
          onClose={() => setEditor({ open: false, connection: null })}
          onSave={handleSave}
        />
      )}

      <ConfirmDialog
        isOpen={pending?.kind === 'rotate'}
        title="Rotate the site key?"
        message="A new site key is issued now. The current key keeps working for 24 hours, then stops. Update the snippet on your website before then or its forms will fail."
        confirmLabel="Rotate key"
        onConfirm={runConfirmed}
        onCancel={() => setPending(null)}
      />
      <ConfirmDialog
        isOpen={pending?.kind === 'disable'}
        title="Turn this connection off?"
        message="Every form on this website will be refused until you turn the connection back on. Nothing already received is deleted."
        confirmLabel="Turn off"
        confirmVariant="danger"
        onConfirm={runConfirmed}
        onCancel={() => setPending(null)}
      />
    </div>
  );
};

export default WebsiteConnectionsPage;
